<script lang="ts">
    import { CURRENT_APP_GROWAVE } from "@/constants/current_app";
    import { STATUS } from "@/constants/statuses";
    import { gwStorefrontAppInfoToken } from "@/tokens";
    import {
        INSTAGRAM_MASONRY_LAYOUT,
        INSTAGRAM_POST_TAG_PRODUCT_TYPE,
        INSTAGRAM_SLIDE_LAYOUT,
    } from "@apps/instagram/constants";
    import type InstagramCustomSettingsEntity from "@apps/instagram/entities/InstagramCustomSettingsEntity";
    import type InstagramDisplaySettingsEntity from "@apps/instagram/entities/InstagramDisplaySettingsEntity";
    import InstagramGalleryQuery from "@apps/instagram/queries/InstagramGalleryQuery";
    import InstagramPostsQuery from "@apps/instagram/queries/InstagramPostsQuery";
    import InstagramStoriesQuery from "@apps/instagram/queries/InstagramStoriesQuery";
    import InstagramTaggedProductQuery from "@apps/instagram/queries/InstagramTaggedProductQuery";
    import type InstagraGalleryServiceInterface from "@apps/instagram/services/InstagramGalleryService/InstagramGalleryServiceInterface";
    import type InstagramPostsServiceInterface from "@apps/instagram/services/InstagramPostsService/InstagramPostsServiceInterface";
    import InstagramStoriesService from "@apps/instagram/services/InstagramStoriesService/InstagramStoriesService";
    import InstagramTaggedProductService from "@apps/instagram/services/InstagramTaggedProductService";
    import InstagramTrackersApiService from "@apps/instagram/services/InstagramTrackersService";
    import InstagramPostsStore from "@apps/instagram/stores/InstagramPostsStore";
    import InstagramStoriesStore from "@apps/instagram/stores/InstagramStoriesStore";
    import QuestionsQuery from "@apps/reviews/queries/QuestionsQuery";
    import ReviewsQuery from "@apps/reviews/queries/ReviewsQueryBase";
    import QuestionsService from "@apps/reviews/services/QuestionsService";
    import ReviewsService from "@apps/reviews/services/ReviewsServiceBase";
    import { setTypedContext } from "@lib/svelte-typed-context";
    import { CartQuery } from "@modules/cart/queries/CartQuery";
    import { CartService } from "@modules/cart/services/CartService";
    import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
    import { CommonModule } from "@modules/common_module/CommonModule";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import { TrackingApiServiceInterface } from "@modules/legacy_tracking/services/TrackingApiServiceInterface";
    import HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
    import { MoneyModule } from "@modules/money/MoneyModule";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
    import { combineLatest, lastValueFrom, of } from "rxjs";
    import { filter, map, startWith, switchMap } from "rxjs/operators";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import ErrorBanner from "@components/ErrorBanner.svelte";

    import InstagramGalleryHeader from "./components/GalleryHeader.svelte";
    import InstagramGallery from "./components/InstagramGallery.svelte";
    import type { WIDGET_TYPE } from "./constants";
    import { PRODUCT_PAGE_WIDGET } from "./constants";
    import { instagramGalleryWidgetContextToken } from "./context_tokens";
    import { combineSettings } from "./utils/combineSettings";
    import masonryPagination from "./utils/masonryPagination";

    export let galleryId: number | null;
    export let galleryLayout: number;
    export let customSettings: InstagramCustomSettingsEntity;
    export let widgetName: WIDGET_TYPE;
    export let locationController: LocationControllerInterface;
    export let instagramGalleryQuery: InstagramGalleryQuery;
    export let instagramGalleryService: InstagraGalleryServiceInterface;
    export let instagramPostsQuery: InstagramPostsQuery;
    export let instagramPostsStore: InstagramPostsStore;
    export let instagramPostsService: InstagramPostsServiceInterface;
    export let reviewsQuery: ReviewsQuery;
    export let reviewsService: ReviewsService;
    export let questionsQuery: QuestionsQuery;
    export let questionsService: QuestionsService;
    export let taggedProductService: InstagramTaggedProductService;
    export let taggedProductQuery: InstagramTaggedProductQuery;
    export let instagramStoriesQuery: InstagramStoriesQuery;
    export let instagramStoriesService: InstagramStoriesService;
    export let instagramStoriesStore: InstagramStoriesStore;
    export let instagramTrackersService: InstagramTrackersApiService;
    export let trackingApiService: TrackingApiServiceInterface;
    export let cartService: CartService;
    export let cartQuery: CartQuery;
    export let productsService: ProductsServiceInterface;
    export let productsQuery: ProductsQuery;
    export let gwi18n: GwI18n;
    export let urlsBuilder: URLsBuilder;
    export let htmlSanitizer: HTMLSanitizerInterface;
    export let cartAvailabilityProvider: CartAvailabilityProvider;

    setTypedContext(instagramGalleryWidgetContextToken, {
        reviewsService,
        reviewsQuery,
        questionsQuery,
        questionsService,
        taggedProductService,
        taggedProductQuery,
        instagramStoriesQuery,
        instagramStoriesService,
        instagramStoriesStore,
        instagramPostsQuery,
        instagramPostsStore,
        instagramPostsService,
        trackingApiService,
        instagramTrackersService,
        cartService,
        cartQuery,
        locationController,
        productsService,
        productsQuery,
        gwi18n,
        urlsBuilder,
        htmlSanitizer,
        cartAvailabilityProvider,
    });
    const { currentApp } = container.resolve(gwStorefrontAppInfoToken);
    const designMode = container
        .resolve(CommonModule)
        .exports.designModeProvider.isDesignMode();

    const moneyModule = container.resolve(MoneyModule);

    const sanitizer = moneyModule.exports.htmlSanitizer;
    const gallery$ = instagramGalleryQuery.gallery$;
    const paginationMeta$ = instagramPostsQuery.paginationMeta$;
    const getGalleryError$ = instagramGalleryQuery.error$;
    const displaySettings$ = gallery$.pipe(
        map(
            (gallery) =>
                gallery?.displaySettings.find((_, idx) => {
                    return idx === galleryLayout;
                }) || null
        ),
        startWith(null)
    );
    const settings$ = combineSettings(displaySettings$, of(customSettings));
    const isFullSettings = (
        settings:
            | (InstagramDisplaySettingsEntity & InstagramCustomSettingsEntity)
            | InstagramCustomSettingsEntity
    ): settings is InstagramDisplaySettingsEntity &
        InstagramCustomSettingsEntity => {
        return "layout" in settings;
    };
    const title$ = combineLatest([settings$, gallery$]).pipe(
        map(([settings, gallery]) => {
            return settings.galleryTitle || gallery?.title || "";
        })
    );
    const sanitizedTitle$ = sanitizer.sanitize$(title$, {
        a: ["class", "href", "target"],
        b: ["class"],
        i: ["class"],
        strong: ["class"],
        span: ["class"],
        small: ["class"],
    });

    const perPage$ = settings$.pipe(
        filter(isFullSettings),
        switchMap((settings) => {
            switch (settings.layout) {
                case INSTAGRAM_MASONRY_LAYOUT:
                    return settings$.pipe(
                        map((settings) => {
                            return (
                                settings as InstagramDisplaySettingsEntity & {
                                    rows: number;
                                }
                            )?.rows;
                        }),
                        masonryPagination(
                            paginationMeta$.pipe(map((pm) => pm.page))
                        )
                    );
                case INSTAGRAM_SLIDE_LAYOUT:
                    return of(16);
                default:
                    return of(settings.rows * settings.columns);
            }
        })
    );
    const posts$ = instagramPostsQuery.all$;
    const status$ = instagramPostsQuery.status$;
    const showTitle$ = posts$.pipe(
        switchMap((posts) => {
            switch (widgetName) {
                case PRODUCT_PAGE_WIDGET:
                    return status$.pipe(
                        filter((status) =>
                            [STATUS.ERROR, STATUS.READY].includes(status)
                        ),
                        map((status) => {
                            return status !== STATUS.ERROR && posts.length;
                        }),
                        startWith(of(true))
                    );
                default:
                    return of(true);
            }
        })
    );
    onMount(async () => {
        lastValueFrom(instagramGalleryService.getGallery());
        lastValueFrom(
            settings$.pipe(
                filter(isFullSettings),
                switchMap((settings) => {
                    return instagramPostsService.getPosts({
                        perPage: $perPage$,
                        postMediaFilter: settings.postMediaFilter,
                        galleryId: $gallery$?.id,
                        sortBy: settings.sortBy,
                        filter: settings.filter,
                    });
                })
            )
        );
        const productsFetchingSubscription = instagramPostsQuery.all$
            .pipe(
                map((posts) => {
                    posts.forEach((post) => {
                        post.tags.forEach((tag) => {
                            if (tag.type === INSTAGRAM_POST_TAG_PRODUCT_TYPE) {
                                productsService.addProductForFetching(
                                    tag.product.id
                                );
                            }
                        });
                    });
                })
            )
            .subscribe();
        return () => {
            productsFetchingSubscription.unsubscribe();
        };
    });
    const loadMorePosts = () => {
        lastValueFrom(
            settings$.pipe(
                filter(isFullSettings),
                switchMap((settings) => {
                    return instagramPostsService.loadMorePosts({
                        perPage: $perPage$,
                        postMediaFilter: settings.postMediaFilter,
                        galleryId: $gallery$?.id,
                        offset:
                            $perPage$ + ($paginationMeta$.currentOffset | 0),
                        sortBy: settings.sortBy,
                        filter: settings.filter,
                    });
                })
            )
        );
    };
</script>

<div class="gw-ig-gallery-widget">
    {#if $gallery$ && $showTitle$}
        <InstagramGalleryHeader>
            {@html $sanitizedTitle$}
        </InstagramGalleryHeader>
    {/if}
    {#if designMode}
        {#if $getGalleryError$}
            <ErrorBanner>
                {#if galleryId}
                    Error on getting gallery by id={galleryId}
                {:else}
                    Error on getting gallery
                {/if}
            </ErrorBanner>
        {/if}
        {#if $gallery$ && !$displaySettings$}
            <ErrorBanner>
                We found gallery with id={$gallery$.id} but not found displaySettings
                with id={galleryLayout}
            </ErrorBanner>
        {/if}
    {/if}
    {#if $gallery$ && $displaySettings$ && isFullSettings($settings$)}
        <InstagramGallery
            gallery={$gallery$}
            isCurrentAppGrowave={currentApp == CURRENT_APP_GROWAVE}
            settings={$settings$}
            {galleryLayout}
            on:loadMore={loadMorePosts}
        />
    {/if}
</div>
